import React from 'react';
import S from 'common/service/sanctuary';
import Datetime from 'common/mdc/form/datetime';
import { func, bool } from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import {
  EMPTY,
  NOT_EMPTY,
  LESS_THAN,
  GREATER_THAN,
  BETWEEN,
  translateOperator,
} from './operator';

import SearchConstraint, { constraintShape } from './constraint';

const messages = defineMessages({
  notEquals: {
    id: 'search.advanced.criteria.constraint.notEquals',
    defaultMessage: 'not {from}',
  },
  greaterThan: {
    id: 'search.advanced.criteria.constraint.greaterThan',
    defaultMessage: '> {from}',
  },
  lessThan: {
    id: 'search.advanced.criteria.constraint.lessThan',
    defaultMessage: '< {from}',
  },
  between: {
    id: 'search.advanced.criteria.constraint.between',
    defaultMessage: 'between {from} and {to}',
  },
  andConjunction: {
    id: 'search.advanced.criteria.constraint.andConjunction',
    defaultMessage: 'AND',
  },
});

const Component = injectIntl(({ from, to, message, intl }) => {
  const formatDate = date => (
    `${intl.formatDate(Date.parse(date))} ${intl.formatTime(Date.parse(date))}`
  );

  return (
    message
      ? <FormattedMessage values={{ from: formatDate(from), to: formatDate(to) }} {...message} />
      : formatDate(from)
  );
});

export const translateConstraint = constraint => S.pipe([
  S.get(S.is(String), 'operator'),
  S.chain(operator => S.toMaybe(({
    [EMPTY]: translateOperator(constraint.operator),
    [NOT_EMPTY]: translateOperator(constraint.operator),
    [GREATER_THAN]: constraint.value ? (
      <Component from={constraint.value[0]} message={messages.greaterThan} />
    ) : null,
    [LESS_THAN]: constraint.value ? (
      <Component from={constraint.value[0]} message={messages.lessThan} />
    ) : null,
    [BETWEEN]: constraint.value ? (
      <Component from={constraint.value[0]} to={constraint.value[1]} message={messages.between} />
    ) : null,
  })[operator])),
])(constraint);

const getContent = (constraint, { onChange, ...props}) => {
  const value = constraint.value || new Array(2);

  const onChangeBetween = (newValue, position) => {
    value[position] = newValue.toISOString();

    if (value.length === 2 && value[0] && value[1]) {
      return onChange(value);
    }
  };

  return ({
    [EMPTY]: null,
    [NOT_EMPTY]: null,
    [LESS_THAN]: <Datetime onChange={date => onChange(date.toISOString())}{...props} />,
    [GREATER_THAN]: <Datetime onChange={date => onChange(date.toISOString())}{...props} />,
    [BETWEEN]: (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Datetime
          onChange={date => onChangeBetween(date, 0)}
          style={{ margin: 0 }}
        />
        <span className="and-conjunction">AND</span>
        <Datetime
          onChange={date => onChangeBetween(date, 1)}
          style={{ margin: 0 }}
        />
      </div>),
  })[constraint.operator];
};

const DateTime = ({
  nullable,
  constraint,
  onValueChange,
  ...props
}) => (
  <SearchConstraint
    {...props}
    constraint={constraint}
    operators={[
      nullable && EMPTY,
      nullable && NOT_EMPTY,
      LESS_THAN,
      GREATER_THAN,
      BETWEEN,
    ].filter(Boolean)}
  >{getContent(constraint, {
    className: 'criteria-input',
    onChange: date => onValueChange(S.is(Array, date) ? date : [date]),
    onKeyDown: (e) => {
      e.stopPropagation();
    },
  })}
  </SearchConstraint>
);

DateTime.propTypes = {
  nullable: bool,
  constraint: constraintShape.isRequired,
  onValueChange: func.isRequired,
};

DateTime.defaultProps = {
  nullable: false,
};

export default DateTime;
